// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { ActionButton, Entity } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// props
export interface DialogUpdateEntityStatusProps {
	entity: Entity;
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogUpdateEntityStatus = ({
	entity,
	isOpen,
	onClose,
	onSuccess,
}: DialogUpdateEntityStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [error, setError] = useState<string | null>(null);

	// vars
	const isActivating = entity.wallet?.status === 'INACTIVE';
	const isSupplier = entity.type === 'SUPPLIER';
	const isBeneficiary = entity.type === 'BENEFICIARY';

	let dialog = {
		alert: isActivating ? t('alert.activateEntity') : t('alert.deactivateEntity'),
		btnSubmit: {
			children: isActivating ? t('activateEntity') : t('deactivateEntity'),
			color: isActivating ? 'primary' : 'error',
		},
		i18nKey: isActivating
			? 'dialogUpdateEntityStatus.activate.content'
			: 'dialogUpdateEntityStatus.deactivate.content',
		title: isActivating ? t('activateEntity') : t('deactivateEntity'),
	};

	// deactivate translations
	if (isSupplier) {
		dialog = {
			alert: isActivating ? t('alert.activateSupplier') : t('alert.deactivateSupplier'),
			btnSubmit: {
				children: isActivating ? t('activateSupplier') : t('deactivateSupplier'),
				color: isActivating ? 'primary' : 'error',
			},
			i18nKey: isActivating
				? 'dialogUpdateSupplierStatus.activate.content'
				: 'dialogUpdateSupplierStatus.deactivate.content',
			title: isActivating ? t('activateSupplier') : t('deactivateSupplier'),
		};
	} else if (isBeneficiary) {
		dialog = {
			alert: isActivating ? t('alert.activateBeneficiary') : t('alert.deactivateBeneficiary'),
			btnSubmit: {
				children: isActivating ? t('activateBeneficiary') : t('deactivateBeneficiary'),
				color: isActivating ? 'primary' : 'error',
			},
			i18nKey: isActivating
				? 'dialogUpdateBeneficiaryStatus.activate.content'
				: 'dialogUpdateBeneficiaryStatus.deactivate.content',
			title: isActivating ? t('activateBeneficiary') : t('deactivateBeneficiary'),
		};
	}

	// activate translations
	if (isActivating) {
		dialog.btnSubmit = {
			children: isSupplier ? t('activateSupplier') : t('activateEntity'),
			color: 'primary',
		};
		dialog.alert = isSupplier ? t('alert.activateSupplier') : t('alert.activateEntity');
		dialog.i18nKey = isSupplier
			? 'dialogUpdateSupplierStatus.activate.content'
			: 'dialogUpdateEntityStatus.activate.content';
		dialog.title = isSupplier ? t('activateSupplier') : t('activateEntity');
	}

	// patch entity
	const { fetchRequest: updateEntity, isLoading: isLoadingUpdateEntity } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities/${entity.id}`,
		options: {
			method: 'PATCH',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
			body: JSON.stringify({
				wallet: {
					status: isActivating ? 'ACTIVE' : 'INACTIVE',
				},
			}),
		},
		onError: (error) => {
			let msg = t('error.default');

			if (error.id === 'ENTITY_UNAUTHORIZED_PATCH') {
				msg = t('error.unauthorized');
			}

			if (error.id === 'ENTITY_WALLET_NON_EXISTENT') {
				msg = t('error.entityWalletNonExistent', { name: entity.name });
			}

			setError(msg);
		},
		onSuccess: () => {
			// set banner
			setBanner({
				hasClose: true,
				message: isActivating
					? t('alert.entityWalletActivated', { name: entity.name })
					: t('alert.entityWalletDeactivated', { name: entity.name }),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		// update entity wallet status
		updateEntity();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setError(null);

		if (onClose) {
			onClose(e);
		}
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					loading: isLoadingUpdateEntity,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'entity',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: entity.name,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.magenta.600" component="span" fontWeight={600} />,
						}}
						i18nKey={dialog.i18nKey}
						values={{ name: entity.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{dialog.alert}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</DialogActions>
	);
};
